import {
  Box,
  HStack,
  HTMLChakraProps,
  ThemingProps,
  useMultiStyleConfig,
} from '@chakra-ui/react'
import type { MaskitoOptions } from '@maskito/core'
import {
  KeyboardEvent,
  type ReactNode,
  Ref,
  useImperativeHandle,
  useRef,
} from 'react'

import { useDeepMemo } from '../../../hooks/useOnChange'
import { Icon } from '../../Icons/Icon'
import {
  BaseInputStylesProvider,
  InputContainer,
  InputField,
  InputSideElement,
} from '../BaseInput/BaseInput.parts'

// Date Mask
const options: MaskitoOptions = {
  mask: [/\d/, /\d/, '-', /\d/, /\d/, '-', /\d/, /\d/, /\d/, /\d/],
}

export type RangeDateInputRefs = {
  containerRef: HTMLInputElement | null
  fromInputRef: HTMLInputElement | null
  toInputRef: HTMLInputElement | null
}

export type RangeDateInputProps = Pick<
  ThemingProps<'BaseInput'>,
  'variant' | 'size'
> &
  HTMLChakraProps<'input'> & {
    leftInputElement?: ReactNode
    rightInputElement?: ReactNode
    maskOptions?: MaskitoOptions
    onUpdate?: ({ to, from }: { to: string; from: string }) => void
    pill?: ReactNode
    autoFocus?: boolean
    ref?: Ref<RangeDateInputRefs>
  }

export const RangeDateInput = ({
  leftInputElement,
  rightInputElement,
  variant = 'primary',
  onUpdate,
  pill,
  autoFocus,
  ref,
  ...props
}: RangeDateInputProps) => {
  const containerRef = useRef<HTMLInputElement>(null)
  const fromInputRef = useRef<HTMLInputElement>(null)
  const toInputRef = useRef<HTMLInputElement>(null)
  const styles = useMultiStyleConfig('BaseInput', { ...props, variant })

  useImperativeHandle(ref, () => ({
    containerRef: containerRef.current,
    fromInputRef: fromInputRef.current,
    toInputRef: toInputRef.current,
  }))

  const [inputProps, rootProps] = useSplitPropsByKeys(props, inputPropsKeys)

  const maskOptions = {
    ...options,
    postprocessors: [
      (args: any) => {
        // We create our own "onChange" because maskquito programmatically updates the value and does not emmit input events.
        if (onUpdate && toInputRef?.current && fromInputRef?.current)
          onUpdate({
            to: toInputRef.current?.value || '',
            from: fromInputRef.current?.value || '',
          })
        return args
      },
    ],
  }

  // focus on the TO input when the length of the FROM input is 10
  const onKeyUp = ({
    key,
  }: {
    key: KeyboardEvent<HTMLInputElement>['key']
  }) => {
    if (fromInputRef.current?.value.length === 10 && isFinite(parseInt(key)))
      toInputRef.current?.focus()
  }

  return (
    <BaseInputStylesProvider value={styles}>
      <InputContainer inputFieldRef={containerRef} {...rootProps}>
        <Box flex="1 1 0" minWidth={0} alignSelf="stretch">
          <HStack gap="4px" alignItems="center">
            {leftInputElement ? (
              <InputSideElement side="left">
                {leftInputElement}
              </InputSideElement>
            ) : null}
            <InputField
              data-testid="date-range-from-input"
              placeholder="mm-dd-yyyy"
              {...inputProps}
              ref={fromInputRef}
              maskOptions={maskOptions}
              onKeyUp={onKeyUp}
              autoFocus={autoFocus}
            />
            <Icon
              icon="ArrowLeftIcon"
              boxSize="8px"
              transform="rotate(180deg)"
              flexShrink={0}
            />
            <InputField
              data-testid="date-range-to-input"
              placeholder="mm-dd-yyyy"
              {...inputProps}
              ref={toInputRef}
              maskOptions={maskOptions}
            />
          </HStack>
        </Box>
        {rightInputElement ? (
          <InputSideElement side="right">{rightInputElement}</InputSideElement>
        ) : null}
      </InputContainer>
    </BaseInputStylesProvider>
  )
}

const splitPropsByKeys = <T extends Record<string, any>>(
  props: T,
  keys: (keyof T | (string & {}))[],
): [Pick<T, (typeof keys)[number]>, Omit<T, (typeof keys)[number]>] => {
  const pickedProps: any = {}
  const restProps: any = {}

  Object.entries(props).forEach(([key, value]) => {
    if (keys.includes(key as any)) {
      pickedProps[key] = value
    } else {
      restProps[key] = value
    }
  })

  return [pickedProps, restProps]
}

const useSplitPropsByKeys = <T extends Record<string, any>>(
  props: T,
  keys: (keyof T | (string & {}))[],
) => {
  return useDeepMemo(() => splitPropsByKeys(props, keys), [props, keys])
}

const inputPropsKeys: (keyof RangeDateInputProps | `data-${string}`)[] = [
  'onClick',
  'onChange',
  'value',
  'id',
  'placeholder',
  'disabled',
  'onFocus',
  'onBlur',
  'type',
  'color',
  'name',
  'data-testid',
  'pattern',
  'autoFocus',
  'step',
  'required',
  'min',
  'max',
  'minLength',
  'maxLength',
]
